/**
 * Top-of-page metric strip: comments total, hidden, deleted, and moderated
 * in the last 24 hours. Source mirrors the admin page-detail counts so the
 * numbers shown to users match what the admin sees one-for-one.
 *
 * Visual design: FacebookPages stats-card pattern (w-10 icon box + p-4 card).
 */
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MessageSquare, EyeOff, Trash2, Activity } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { useApi, API_ENDPOINTS } from "../lib/api";
import { cn } from "../lib/utils";

interface ImpactData {
  commentsTotal: number;
  hidden: number;
  deleted: number;
  moderated24h: number;
}

export type ImpactCardKey = "comments" | "hidden" | "deleted" | "moderated24h";

const REFRESH_INTERVAL_MS = 60_000;

interface Props {
  pageId: string;
  className?: string;
  /** When provided, each stat card becomes a clickable filter toggle. */
  onCardClick?: (key: ImpactCardKey) => void;
  /** Highlights the card matching the currently active filter. */
  activeKey?: ImpactCardKey | null;
}

export const ImpactStrip: React.FC<Props> = ({
  pageId,
  className,
  onCardClick,
  activeKey,
}) => {
  const api = useApi();
  const [data, setData] = useState<ImpactData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // `api` is intentionally excluded from deps: useApi() returns a fresh object
  // every render, so including it would re-fire this effect on every render
  // and hammer the backend.
  const apiRef = useRef(api);
  useEffect(() => {
    apiRef.current = api;
  });

  useEffect(() => {
    if (!pageId) return;
    let cancelled = false;

    const fetchImpact = async () => {
      try {
        const res = await apiRef.current.get(API_ENDPOINTS.IMPACT(pageId));
        if (cancelled || !mountedRef.current) return;
        setData(res.data);
        setError(null);
      } catch (err: any) {
        if (cancelled || !mountedRef.current) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load");
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    };

    setLoading(true);
    fetchImpact();
    const interval = setInterval(fetchImpact, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pageId]);

  const display = (n: number | undefined) =>
    loading || data === null ? "—" : (n ?? 0).toLocaleString();

  return (
    <div className={cn("mb-8", className)}>
      <div className="grid grid-cols-4 gap-2 md:gap-4">
        <StatCard
          delay={0}
          icon={MessageSquare}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          label="Comments"
          value={display(data?.commentsTotal)}
          error={error}
          onClick={onCardClick ? () => onCardClick("comments") : undefined}
          active={activeKey === "comments"}
        />
        <StatCard
          delay={0.1}
          icon={EyeOff}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          label="Hidden"
          value={display(data?.hidden)}
          error={error}
          onClick={onCardClick ? () => onCardClick("hidden") : undefined}
          active={activeKey === "hidden"}
        />
        <StatCard
          delay={0.2}
          icon={Trash2}
          iconBg="bg-red-100"
          iconColor="text-red-600"
          label="Deleted"
          value={display(data?.deleted)}
          error={error}
          onClick={onCardClick ? () => onCardClick("deleted") : undefined}
          active={activeKey === "deleted"}
        />
        <StatCard
          delay={0.3}
          icon={Activity}
          iconBg="bg-green-100"
          iconColor="text-green-600"
          label="Moderated 24h"
          value={display(data?.moderated24h)}
          error={error}
          onClick={onCardClick ? () => onCardClick("moderated24h") : undefined}
          active={activeKey === "moderated24h"}
        />
      </div>
    </div>
  );
};

interface StatCardProps {
  delay: number;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  error: string | null;
  onClick?: () => void;
  active?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  delay,
  icon: Icon,
  iconBg,
  iconColor,
  label,
  value,
  error,
  onClick,
  active,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
  >
    <Card
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        onClick && "cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5",
        active && "ring-2 ring-blue-500 shadow-md"
      )}
    >
      <CardContent className="p-2 md:p-4">
        <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left">
          <div className="flex-shrink-0">
            <div
              title={label}
              className={cn(
                "w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center",
                iconBg
              )}
            >
              <Icon className={cn("h-4 w-4 md:h-5 md:w-5", iconColor)} />
            </div>
          </div>
          <div className="mt-1 md:mt-0 md:ml-3">
            <p className="hidden md:block text-xs font-medium text-gray-500">{label}</p>
            <p className="text-sm md:text-xl font-bold text-gray-900 tabular-nums">
              {error ? "—" : value}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

export default ImpactStrip;
